import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Layout,
  Popconfirm,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import { ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { PageHeader } from "@ant-design/pro-layout";
import { HealthResponse, HistoryResponse } from "../../types/crawlerHealth";
import { WizardService } from "../../services/WizardService";
import { CronStatusCard } from "./CronStatusCard";
import { LastRunCard } from "./LastRunCard";
import { RunHistoryTable } from "./RunHistoryTable";

const { Content } = Layout;
const { Text } = Typography;

const POLL_INTERVAL_MS = 30_000;

export const SystemHealthPage: React.FC = () => {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = useCallback(() => {
    setHealthLoading(true);
    WizardService.CrawlerMonitor.health()
      .then((data) => setHealth(data))
      .catch(() => {})
      .finally(() => setHealthLoading(false));
  }, []);

  const fetchHistory = useCallback(
    (page: number = currentPage, size: number = pageSize) => {
      setHistoryLoading(true);
      const offset = (page - 1) * size;
      WizardService.CrawlerMonitor.history(size, offset)
        .then((data) => setHistory(data))
        .catch(() => {})
        .finally(() => setHistoryLoading(false));
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    fetchHealth();
    fetchHistory(1, pageSize);

    pollTimerRef.current = setInterval(() => {
      fetchHealth();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchHistory(page, size);
  };

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerError(null);
    try {
      await WizardService.CrawlerMonitor.trigger();
      fetchHealth();
      fetchHistory(1, pageSize);
      setCurrentPage(1);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ?? err?.message ?? "Trigger failed";
      setTriggerError(message);
    } finally {
      setTriggering(false);
    }
  };

  const isRunning = health?.isRunning ?? false;
  const cronDisabled = health ? !health.cron.enabled : false;
  const isBusy = isRunning || triggering;

  return (
    <Layout>
      <Content>
        <PageHeader
          title="System Health"
          subTitle="Crawler monitor and run history"
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchHealth();
                fetchHistory(currentPage, pageSize);
              }}
              loading={healthLoading}
            >
              Refresh
            </Button>
          }
        />

        {triggerError && (
          <Alert
            type="error"
            message={triggerError}
            closable
            onClose={() => setTriggerError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <CronStatusCard cron={health?.cron ?? null} loading={healthLoading} />
          </Col>

          <Col xs={24} sm={12} md={8}>
            {/* KI-39, 2026-09-18: `emptyProviders` rides the same 30s `health()` poll as
                `lastRun` and is scoped to that same run, so it belongs on this card rather than
                in its own box. Passed through as-is — it may be `undefined` on the wire. */}
            <LastRunCard
              lastRun={health?.lastRun ?? null}
              emptyProviders={health?.emptyProviders}
              loading={healthLoading}
            />
          </Col>

          <Col xs={24} sm={24} md={8}>
            <Card title="Current Status">
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div>
                  {isBusy ? (
                    <Space>
                      <Spin size="small" />
                      <Text type="warning">Crawl in progress...</Text>
                    </Space>
                  ) : cronDisabled ? (
                    <Badge status="warning" text="Cron disabled" />
                  ) : (
                    <Badge status="success" text="Idle" />
                  )}
                </div>
                <Popconfirm
                  title="Trigger full crawl?"
                  description="This will reindex all providers on both platforms."
                  onConfirm={handleTrigger}
                  okText="Yes, trigger"
                  cancelText="Cancel"
                  disabled={isBusy}
                >
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={triggering}
                    disabled={isBusy}
                    block
                  >
                    {isBusy ? "Running..." : "Trigger Crawl"}
                  </Button>
                </Popconfirm>
              </Space>
            </Card>
          </Col>
        </Row>

        <Card title="Run History">
          <RunHistoryTable
            history={history}
            loading={historyLoading}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            pageSize={pageSize}
          />
        </Card>
      </Content>
    </Layout>
  );
};
